import { CIO_WRITE_KEY, USERPILOT_TOKEN } from "./firebase.js";
import { isStaging } from "./environments";
import { Userpilot } from "userpilot";
import { AnalyticsBrowser } from "@customerio/cdp-analytics-browser";
import { useFeatureFlagStore } from "../featureFlags/useFeatureFlagStore";

let initialized = false;
let cioAnalytics: AnalyticsBrowser | null = null;

// Impersonation context — set by ImpersonationProvider
let _impersonationProps: AnyProperties = {};

export function setImpersonationContext(props: AnyProperties) {
  _impersonationProps = props;
}

// Surface context — set once by non-website surfaces (the SketchUp panel
// sets {surface: 'sketchup'}) and merged into every trackEvent/pageView the
// same way _impersonationProps is. The website never sets it, so its events
// are byte-identical.
let _surfaceProps: AnyProperties = {};

export function setSurfaceContext(props: AnyProperties) {
  _surfaceProps = props;
}

// Surface identify traits — merged into identifyUser's metadata only (not
// events). The SketchUp panel sets {sketchupUser: true, extensionVersion} so
// panel users are segmentable on their profile, not just by event history.
let _surfaceTraits: AnyProperties = {};

export function setSurfaceTraits(traits: AnyProperties) {
  _surfaceTraits = traits;
}

// Per-surface analytics configuration. skipUserpilot keeps Userpilot from
// ever initializing on a surface (the guards below stop its npm wrapper
// queueing calls forever against a loader that never attaches). No surface
// sets it today — the SketchUp panel did until v0.7.0, and gets it back if
// broadly-targeted Userpilot content ever threatens to render in the panel.
let _skipUserpilot = false;

export function configureAnalytics(options: { skipUserpilot?: boolean }) {
  _skipUserpilot = Boolean(options.skipUserpilot);
}

// Team-Pro context — pushed by LoginState.updateTeamsProAccess whenever the
// membership-wide "active designer in a team" signal is (re)computed.
//
// Team Pro is DECOUPLED from profiles.userRole (a team designer with no
// personal Stripe sub is userRole=basic; see docs/workspaces-architecture.md),
// so identify()'s userRole trait alone undercounts the paying Teams cohort.
// We inject the signal here rather than importing the LoginState store so this
// module stays free of the store's Firebase dependencies — mirroring the
// impersonation-context pattern above.
let _isTeamPro = false;

// GA4's user_id follows the cookie choice (set via setGAUserIdAllowed), not
// whether Userpilot/Customer.io have started, so it isn't lost to their init.
let _gaUserIdAllowed = false;
let _gaUserId: string | null = null;
// The account just signed out: a late identify for it is stale until the next sign-in.
let _signedOutUid: string | null = null;

function applyGAUserId() {
  try {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const gtag = typeof window !== "undefined" ? (window as any).gtag : undefined;
    if (typeof gtag === "function") gtag("set", { user_id: _gaUserIdAllowed ? _gaUserId : null });
  } catch (error) {
    console.error("GA user_id", error);
  }
}

/** The auth listener saw a sign-in, so identifies are current again. */
export function noteAnalyticsSignIn() {
  _signedOutUid = null;
}

/** Outside the EU, or in it after an accept: GA4 may carry the account id. */
export function setGAUserIdAllowed(allowed: boolean) {
  if (allowed === _gaUserIdAllowed) return;
  _gaUserIdAllowed = allowed;
  if (_gaUserId) applyGAUserId();
}

export function setGAUserId(uid: string | null) {
  if (uid !== null && uid === _signedOutUid) return;
  _gaUserId = uid;
  if (_gaUserIdAllowed || uid === null) applyGAUserId();
}

// The active team workspace (Team Trends); unset in Personal. GA4 drops custom
// keys given to gtag("set"), so it goes inline on page views and trackEventGA
// events, and as a user property on every hit.
let _gaActiveFirm: string | null = null;
let _gaActiveFirmPending = false;

function applyGAActiveFirm() {
  try {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const gtag = typeof window !== "undefined" ? (window as any).gtag : undefined;
    _gaActiveFirmPending = typeof gtag !== "function";
    // Object form: Firebase's gtag wrapper drops a third argument. gtag sends a
    // user property once per change and GA4 keeps it, so Personal sends null to clear it.
    if (!_gaActiveFirmPending) gtag("set", { user_properties: { active_firm_id: _gaActiveFirm } });
  } catch (error) {
    console.error("GA active_firm_id", error);
  }
}

const activeFirmParam = () => (_gaActiveFirm ? { active_firm_id: _gaActiveFirm } : {});

/** The SPA page view; it carries the active team (Team Trends counts team days from it). */
export function gaPageView(path: string) {
  try {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const gtag = typeof window !== "undefined" ? (window as any).gtag : undefined;
    if (typeof gtag !== "function") return;
    if (_gaActiveFirmPending) applyGAActiveFirm();
    gtag("set", "page_path", path);
    gtag("event", "page_view", activeFirmParam());
  } catch (error) {
    console.error("GA page_view", error);
  }
}

export function setGAActiveFirm(firmId: string | null) {
  if (firmId === _gaActiveFirm) return;
  _gaActiveFirm = firmId;
  applyGAActiveFirm();
}

export function setTeamProContext(isTeamPro: boolean) {
  _isTeamPro = isTeamPro;
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function handleCreatedAt(createdAt: any) {
  /**
   * Handles createdAt timestamp formatting for Userpilot
   * - Userpilot expects a string, not a Firebase object
   * - createdAt seems to have been in various formats
   * - duplicated in bulk_identify.cjs
   * @param createdAt - Firebase timestamp, number, or null
   * @returns ISO string date or null
   */

  if (!createdAt) {
    return "";
  }

  if (typeof createdAt.toDate === "function") {
    return createdAt.toDate().toISOString();
  }

  if (typeof createdAt === "number") {
    return new Date(createdAt).toISOString();
  }

  if (typeof createdAt === "object" && typeof createdAt.seconds === "number") {
    // Convert seconds to milliseconds
    return new Date(createdAt.seconds * 1000).toISOString();
  }

  if (createdAt instanceof Date) {
    return createdAt.toISOString();
  }

  return "";
}

// Init waits on the geo lookup and, in the EU, on consent; trackEvent drops
// anything sent before it, so mount-time callers wait on this.
const initListeners = new Set<() => void>();

export function isAnalyticsInitialized() {
  return initialized;
}

/** Calls cb once analytics is initialized (now, if it already is). */
export function onAnalyticsInitialized(cb: () => void): () => void {
  if (initialized) {
    cb();
    return () => undefined;
  }
  initListeners.add(cb);
  return () => {
    initListeners.delete(cb);
  };
}

export function initAnalytics() {
  // only init once
  if (initialized) return;
  if (!_skipUserpilot) {
    try {
      Userpilot.initialize(USERPILOT_TOKEN);
    } catch (error) {
      console.error("UP initialize", error);
    }
  }
  try {
    cioAnalytics = AnalyticsBrowser.load({ writeKey: CIO_WRITE_KEY });
  } catch (error) {
    console.error("CIO load", error);
  }
  initialized = true;
  const listeners = [...initListeners];
  initListeners.clear();
  for (const cb of listeners) {
    try {
      cb();
    } catch (error) {
      console.error("analytics init listener", error);
    }
  }
}

function handleName(profile) {
  const firstName = profile?.first_name || "";
  const lastName = profile?.last_name || "";
  if (firstName && lastName) {
    return firstName + " " + lastName;
  }
  if (firstName) {
    return firstName;
  }
  if (lastName) {
    return firstName || lastName;
  }
  return "";
}

// Userpilot won't fire any events (including page views) until identify or
// anonymous is called — window.userpilot.initialized sits at -1 otherwise.
// The npm wrapper queues calls per-method and flushes them once the loader
// script attaches window.userpilot, so we don't need to wait for it ourselves.
export function identifyAnonymous() {
  if (!initialized) return;
  if (_skipUserpilot) return;
  try {
    Userpilot.anonymous();
  } catch (error) {
    console.error("UP identifyAnonymous", error);
  }
}

export function identifyUser({
  currentUser,
  profile,
  metadata: extraMetadata = {},
}) {
  // Before the init check: GA4 is gated on consent alone (see setGAUserIdAllowed).
  if (currentUser?.uid) setGAUserId(currentUser.uid);
  if (!initialized) return;

  // Return early if currentUser is completely missing
  if (!currentUser || !currentUser.uid) {
    console.warn("identifyUser: currentUser or currentUser.uid is missing");
    return;
  }

  // Determine email: prefer currentUser.email, fallback to profile.email
  const email = currentUser.email || profile?.email;

  if (!email) {
    console.warn("identifyUser: No email available in currentUser or profile");
    return;
  }

  // Forward feature-flag verdicts as `ff_<key>` traits so Customer.io/Userpilot
  // can build lists/segments by feature exposure. Verdicts load async after
  // login (see FeatureFlagsProvider), so identify is re-fired once they land.
  const flagTraits = Object.fromEntries(
    Object.entries(useFeatureFlagStore.getState().flags).map(([key, value]) => [
      `ff_${key}`,
      value,
    ]),
  );

  const metadata = {
    name: handleName(profile),
    email: email,
    company: profile?.company_name || "",
    companySize: profile?.company_size || "",
    country: profile?.country || "",
    industry: profile?.industry || "",
    occupation: profile?.occupation || "",
    specialization: profile?.specialization || "",
    userRole: profile?.userRole || "",
    // Effective Pro traits. `userRole` reflects only PERSONAL entitlement; team
    // Pro is derived per workspace and injected via setTeamProContext. `isPro`
    // is the effective headcount (personal OR team) so Pro-vs-Basic dashboards
    // don't cliff when the team-Pro decoupling clears userRole for team-only
    // designers.
    isPersonalPro: (profile?.userRole || "") === "pro",
    isTeamPro: _isTeamPro,
    isPro: (profile?.userRole || "") === "pro" || _isTeamPro,
    created_at: handleCreatedAt(profile?.created_at),
    // Persistent "signed up in SketchUp" segment; empty on older profiles.
    signupSurface: profile?.signupSurface || "",
    ...flagTraits,
    ..._surfaceTraits,
    ...extraMetadata,
  };

  if (!_skipUserpilot) {
    try {
      Userpilot.identify(currentUser.uid, metadata);
    } catch (error) {
      console.error("UP identifyUser", error);
    }
  }


  if (cioAnalytics) {
    try {
      cioAnalytics.identify(currentUser.uid, metadata);
    } catch (error) {
      console.error("CIO identifyUser", error);
    }
  }
}

export interface AnyProperties {
  [key: string]: string | number | boolean | null | undefined;
}

export function trackEvent(event: string, properties?: AnyProperties) {
  if (!initialized) {
    console.warn(
      "[analyticsManager] trackEvent called but analytics not initialized. Event dropped:",
      event,
    );
    return;
  }
  const props = {
    ...properties,
    ..._impersonationProps,
    ..._surfaceProps,
  };

  if (!_skipUserpilot) {
    try {
      Userpilot.track(event, props);
    } catch (error) {
      console.error("UP trackEvent", error);
    }
  }

  if (cioAnalytics) {
    try {
      cioAnalytics.track(event, props);
    } catch (error) {
      console.error("CIO trackEvent", error);
    }
  }
}

// Fires an event to GA4 (window.gtag) only — not Userpilot/Customer.io.
// Use alongside trackEvent for events that should also appear in GA4 reports.
export function trackEventGA(event: string, properties?: AnyProperties) {
  try {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const gtag =
      typeof window !== "undefined" ? (window as any).gtag : undefined;
    if (typeof gtag === "function") {
      if (_gaActiveFirmPending) applyGAActiveFirm();
      gtag("event", event, {
        ...activeFirmParam(),
        ...properties,
        ..._impersonationProps,
        ..._surfaceProps,
      });
    }
  } catch (error) {
    console.error("GA trackEventGA", error);
  }
}

// Base code is in index.html, on mattoboard.com hosts only — so guard on window.fbq.
function callFbq(...args: unknown[]) {
  try {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const fbq = typeof window !== "undefined" ? (window as any).fbq : undefined;
    if (typeof fbq === "function") fbq(...args);
  } catch (error) {
    console.error("Meta fbq", error);
  }
}

// eventID is the CAPI dedup key. Impersonated sessions never convert.
export function trackEventMeta(
  name: string,
  params: Record<string, unknown> = {}, // Meta params may hold arrays (content_ids)
  eventID?: string,
) {
  // daily.mattoboard.com shares the production pixel. Keep test-mode purchases
  // and QA signups out of the real conversion dataset. Consent calls and the
  // index.html PageView still fire on staging, so the gate stays testable.
  // Read from environments, not firebase.js: that module is mocked piecemeal
  // across the suite, and a missing named export throws at access time.
  if (isStaging()) return;
  if (Object.keys(_impersonationProps).length) return;
  callFbq("track", name, params, eventID ? { eventID } : undefined);
}

// index.html revokes before init unless cookieConsent is "accepted".
export function setMetaConsent(granted: boolean) {
  callFbq("consent", granted ? "grant" : "revoke");
}

export type MetaActivationSource =
  | "drag_material"
  | "click_material"
  | "save_board"
  | "concept_started"
  | "custom_material"
  | "custom_paint";

// One Meta Lead per user, on the first activation action. The key is set before
// firing on purpose: a revoked pixel queues and flushes on grant, a declined one drops.
export function trackMetaActivationOnce(
  uid: string | null | undefined,
  source: MetaActivationSource,
) {
  if (!uid) return;
  const key = `meta_lead_fired:${uid}`;
  try {
    if (localStorage.getItem(key)) return;
    localStorage.setItem(key, source);
  } catch {
    return;
  }
  trackEventMeta("Lead", { content_name: source }, `lead_${uid}`);
}

export function pageView() {
  if (!initialized) return;

  if (!_skipUserpilot) {
    try {
      Userpilot.reload();
    } catch (error) {
      console.error("UP pageView", error);
    }
  }

  if (cioAnalytics) {
    try {
      // A lone object argument is the Segment-style properties overload; the
      // website (no surface context) keeps the bare call.
      if (Object.keys(_surfaceProps).length > 0) cioAnalytics.page(_surfaceProps);
      else cioAnalytics.page();
    } catch (error) {
      console.error("CIO pageView", error);
    }
  }
}

export function logoutAnalytics() {
  // Detach the GA4 user_id so post-logout events (or the next account on a
  // shared browser) aren't attributed to the previous user. It can be set
  // without Userpilot/Customer.io running, so this comes before their check.
  // Logout runs twice (account menu and auth listener); keep the first id.
  if (_gaUserId !== null) _signedOutUid = _gaUserId;
  setGAUserId(null);
  if (!initialized && !cioAnalytics) return;

  if (!_skipUserpilot) {
    try {
      Userpilot.destroy();
    } catch (error) {
      console.error("UP logoutAnalytics", error);
    }
  }

  if (cioAnalytics) {
    try {
      cioAnalytics.reset();
      cioAnalytics = null;
    } catch (error) {
      console.error("CIO logoutAnalytics", error);
    }
  }

  initialized = false;
}

export function disableAnalytics() {
  // destroy() stops Userpilot; clearing window.userpilot left its timers running and throwing (#4414).
  // Only once initialized: before that Userpilot hasn't started, and its loader would only queue the call.
  if (initialized && !_skipUserpilot) {
    try {
      Userpilot.destroy();
    } catch (error) {
      console.error("UP disableAnalytics", error);
    }
  }
  initialized = false;
}
